import { Component, HostListener, Inject, Input, Optional } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
  selector: 'app-webinar-images',
  templateUrl: './webinar-images.component.html',
  styleUrl: './webinar-images.component.css',
  standalone: false
})
export class WebinarImagesComponent {
  // Pass array of image URLs from parent
  @Input() images: string[] = [];

  constructor(@Optional() @Inject(MAT_DIALOG_DATA) data?: string[]) {
    if (Array.isArray(data) && data.length) {
      this.images = data;
    }
  }

  // Carousel index
  currentIndex = 0;

  // Gallery & zoom state
  showGallery = false;
  galleryIndex = 0; // Index for gallery carousel
  zoomIndex: number | null = null;
  zoomLevel = 1;

  // =====================
  // Carousel prev / next
  // =====================
  prev(): void {
    if (!this.images || this.images.length === 0) return;
    this.currentIndex =
      (this.currentIndex - 1 + this.images.length) % this.images.length;
  }

  next(): void {
    if (!this.images || this.images.length === 0) return;
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
  }

  // =====================
  // 1) Open gallery carousel
  // =====================
  openGallery(index: number): void {
    if (!this.images || this.images.length === 0) return;
    this.galleryIndex = index;
    this.showGallery = true;
    this.zoomIndex = null; // ensure zoom is closed
  }

  closeGallery(): void {
    this.showGallery = false;
  }

  // Gallery carousel navigation
  prevGallery(): void {
    if (!this.images || this.images.length === 0) return;
    this.galleryIndex = (this.galleryIndex - 1 + this.images.length) % this.images.length;
  }

  nextGallery(): void {
    if (!this.images || this.images.length === 0) return;
    this.galleryIndex = (this.galleryIndex + 1) % this.images.length;
  }

  // =====================
  // 2) Open zoom view
  // =====================
  openZoom(index: number, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    if (!this.images || this.images.length === 0) return;

    this.zoomIndex = index;
    this.zoomLevel = 1;
  }

  closeZoom(): void {
    this.zoomIndex = null;
    this.zoomLevel = 1;
  }

  // Zoom controls
  zoomIn(event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.zoomLevel = Math.min(this.zoomLevel + 0.25, 10);
  }

  zoomOut(event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.zoomLevel = Math.max(this.zoomLevel - 0.25, 0.1);
  }

  resetZoom(event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.zoomLevel = 1;
  }

  // Navigate between images in zoom mode
  prevZoom(event?: MouseEvent): void {
    if (event) event.stopPropagation();
    if (this.zoomIndex === null || this.images.length === 0) return;
    this.zoomIndex =
      (this.zoomIndex - 1 + this.images.length) % this.images.length;
    this.zoomLevel = 1;
  }

  nextZoom(event?: MouseEvent): void {
    if (event) event.stopPropagation();
    if (this.zoomIndex === null || this.images.length === 0) return;
    this.zoomIndex = (this.zoomIndex + 1) % this.images.length;
    this.zoomLevel = 1;
  }

  // =====================
  // Keyboard shortcuts
  // =====================
  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    // ESC closes zoom first, then gallery
    if (event.key === 'Escape') {
      if (this.zoomIndex !== null) {
        this.closeZoom();
        return;
      }
      if (this.showGallery) {
        this.closeGallery();
        return;
      }
    }

    // Left / Right arrows move in gallery or zoom view
    if (this.showGallery) {
      if (event.key === 'ArrowLeft') this.prevGallery();
      if (event.key === 'ArrowRight') this.nextGallery();
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.openZoom(this.galleryIndex);
      }
    } else if (this.zoomIndex !== null) {
      if (event.key === 'ArrowLeft') this.prevZoom();
      if (event.key === 'ArrowRight') this.nextZoom();
    }
  }
}
